/**
 * Screen-1 (painPoints) dwell measurement, BOTH organic arms
 * (docs/FUNNEL-ORGANIC-V2.md). One GA4 `funnel_screen1_exit` per visit:
 *   - outcome 'continue' — the visitor tapped the CTA
 *   - outcome 'leave'    — the tab was hidden or the page unloaded (pagehide)
 *     while screen 1 was showing; sent with transport_type 'beacon'
 * Params: dwell_ms (integer), dwell_bucket, plus experiment_version +
 * enrollment_id from trackEvent. The once-per-visit flag lives in
 * sessionStorage so going back to screen 1 cannot fire a second event.
 *
 * `unload` is deliberately not listened to: pagehide covers it on every
 * current browser, and an unload listener disables the back/forward cache.
 */
import { useCallback, useEffect } from 'react'
import { createScreen1ExitTracker, type Screen1ExitParams } from './experimentCore'
import { trackEvent } from './analytics'

const SENT_KEY = 'kidneypal_screen1_exit_sent'

function now(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now()
}

const tracker = createScreen1ExitTracker({
  now,
  send: (p: Screen1ExitParams) =>
    trackEvent('funnel_screen1_exit', p.outcome === 'leave' ? { ...p, transport_type: 'beacon' } : { ...p }),
  alreadySent: () => {
    try {
      return sessionStorage.getItem(SENT_KEY) === '1'
    } catch {
      return false
    }
  },
  markSent: () => {
    try {
      sessionStorage.setItem(SENT_KEY, '1')
    } catch {}
  },
})

/**
 * Start the dwell clock while screen 1 is mounted and report a 'leave' on tab
 * hide / pagehide. Returns the handler to call when the visitor continues.
 */
export function useScreen1ExitTracking(): () => void {
  useEffect(() => {
    tracker.start()
    const onHidden = () => {
      if (document.visibilityState === 'hidden') tracker.exit('leave')
    }
    const onPageHide = () => tracker.exit('leave')
    document.addEventListener('visibilitychange', onHidden)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      document.removeEventListener('visibilitychange', onHidden)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [])

  return useCallback(() => {
    tracker.exit('continue')
  }, [])
}
